import { z } from "zod";

/**
 * Shared copy and configuration for the Monthly Climate Comparison page, its
 * embed, the chart and the series hook. Kept in one place so the page and the
 * Drupal embed cannot drift apart.
 */

/* The two weather stations the basin reports on. Station 155 is the one with a
   long enough record to average against, so it supplies the normal. */
export const STATIONS = {
  KAPA: {
    locationid: 155,
    label: "KAPA",
    longLabel: "Denver Centennial Airport (KAPA)",
  },
  MHFD: {
    locationid: 156,
    label: "Cherry Creek MET",
    longLabel: "Cherry Creek MET station (MHFD)",
    /* First reading 2021-06-17, so water year 2022 is its first complete one. */
    firstCompleteWaterYear: 2022,
    recordStarts: "June 2021",
  },
};

/* The three things the chart can draw. `field` names the column on
   /api/climate-monthly, `normalField` the matching column on its /normals. */
export const VARIABLES = {
  precip: {
    value: "precip",
    label: "Precipitation total",
    shortLabel: "Precipitation",
    field: "precip_total_in",
    normalField: "precip_normal_in",
    unit: "in",
    decimals: 2,
    isTemperature: false,
    axisLabel: "Precipitation (in)",
    /* Only a precipitation chart gets a water year total row; averaging
       twelve monthly mean temperatures into one number is not meaningful. */
    hasWaterYearTotal: true,
  },
  tmax: {
    value: "tmax",
    label: "Average daily maximum temperature",
    shortLabel: "Avg daily max temp",
    field: "tmax_avg_f",
    normalField: "tmax_normal_f",
    unit: "°F",
    decimals: 1,
    isTemperature: true,
    axisLabel: "Average daily maximum temperature",
    hasWaterYearTotal: false,
  },
  tmin: {
    value: "tmin",
    label: "Average daily minimum temperature",
    shortLabel: "Avg daily min temp",
    field: "tmin_avg_f",
    normalField: "tmin_normal_f",
    unit: "°F",
    decimals: 1,
    isTemperature: true,
    axisLabel: "Average daily minimum temperature",
    hasWaterYearTotal: false,
  },
};

export const VARIABLE_OPTIONS = Object.values(VARIABLES).map((v) => ({
  label: v.label,
  value: v.value,
}));

/* Water year n runs 1 October n-1 to 30 September n, so October leads. */
export const WY_MONTHS = [
  { order: 1, monthNum: 10, abbrev: "Oct", days: 31 },
  { order: 2, monthNum: 11, abbrev: "Nov", days: 30 },
  { order: 3, monthNum: 12, abbrev: "Dec", days: 31 },
  { order: 4, monthNum: 1, abbrev: "Jan", days: 31 },
  { order: 5, monthNum: 2, abbrev: "Feb", days: 28 }, // 29 in a leap year, handled below
  { order: 6, monthNum: 3, abbrev: "Mar", days: 31 },
  { order: 7, monthNum: 4, abbrev: "Apr", days: 30 },
  { order: 8, monthNum: 5, abbrev: "May", days: 31 },
  { order: 9, monthNum: 6, abbrev: "Jun", days: 30 },
  { order: 10, monthNum: 7, abbrev: "Jul", days: 31 },
  { order: 11, monthNum: 8, abbrev: "Aug", days: 31 },
  { order: 12, monthNum: 9, abbrev: "Sep", days: 30 },
];

/** Calendar days in a water-year month, leap Februaries included. */
export const daysInWyMonth = (waterYear, monthNum) => {
  const calendarYear = monthNum >= 10 ? waterYear - 1 : waterYear;
  return new Date(calendarYear, monthNum, 0).getDate();
};

/** Water year n runs 1 Oct n-1 to 30 Sep n. */
export const waterYearOf = (date) =>
  date.getMonth() >= 9 ? date.getFullYear() + 1 : date.getFullYear();

export const CURRENT_WATER_YEAR = waterYearOf(new Date());

/* The annual report figure averages from water year 2006. KAPA data start in
   July 1998, so 1999 is the earliest complete water year to start from. */
export const DEFAULT_NORMAL_START = 2006;
export const EARLIEST_NORMAL_START = 1999;

export const MAX_COMPARE_YEARS = 3;

/* How many water years to offer as comparison chips. The record runs back to
   1998, which is 29 chips over two rows - too many to scan, and the oldest are
   part years nobody compares against. Reservoir Inflow Comparisons shows about
   a dozen, so this matches. A year already selected in the URL is always shown
   even if it falls outside the window, or it could not be switched off. */
export const COMPARE_YEARS_SHOWN = 12;

/* Every water year that can open a normal period: 1999 through the last
   complete one. A select rather than a free number box, so an out-of-range
   year cannot be typed, and so the input matches every other filter on the
   site. */
export const NORMAL_START_OPTIONS = Array.from(
  { length: CURRENT_WATER_YEAR - EARLIEST_NORMAL_START },
  (_, i) => {
    const year = EARLIEST_NORMAL_START + i;
    return { label: String(year), value: String(year) };
  }
);

export const UNIT_OPTIONS = [
  { label: "\u00B0F", value: "f" },
  { label: "\u00B0C", value: "c" },
];

export const PAGE_TITLE = "Monthly Climate Comparison";

/* Two sentences on the stations and the normal period, per the plan. */
export const ABOUT_STATIONS =
  "Precipitation and air temperature are measured at two stations in the basin: " +
  "Denver Centennial Airport (KAPA), a National Weather Service station with a " +
  "record back to July 1998, and the Cherry Creek MET station operated by Mile " +
  "High Flood District, which has reported since June 2021.";

export const ABOUT_NORMAL =
  "Each month is compared against the KAPA long-term average for that same " +
  "water-year month, taken over complete water years only. The annual report " +
  "uses 2006 through the most recent complete water year, which is the default " +
  "here; you can move the start year back as far as 1999.";

/* Measured against the live data on 2026-10-03, over the 60 months the two
   stations overlap (WY2022 to WY2026). The gauges are 4.5 miles apart, so
   little of this is real climate difference. */
export const MHFD_PRECIP_CAVEAT =
  "The Cherry Creek MET gauge is a tipping bucket, which catches frozen " +
  "precipitation poorly. On days cold enough to snow (KAPA daily maximum at " +
  "or below 35\u00B0F) it recorded 44% of KAPA's precipitation; on plain rain " +
  "days (45 to 60\u00B0F) the two agree within 7%. Read its winter bars as a " +
  "lower bound rather than a measurement. The long-term average is KAPA's " +
  "alone, so it is a fair comparison for the KAPA bars and only a rough one " +
  "for Cherry Creek MET.";

export const CHART_NOTE =
  "Bars run October to September to follow the water year, with the long-term " +
  "average drawn as a line across them. A month with no data " +
  "at a station is left blank rather than drawn as zero, and a month that is " +
  "still in progress or missing days is drawn faded and listed beneath the chart.";

/**
 * The query-parameter shape the page and the embed share, so a URL copied from
 * one works in the other. The embed adds `controls` and `height` on top.
 */
export const CLIMATE_PARAM_SHAPE = {
  variable: z.enum(["precip", "tmax", "tmin"]).default("precip"),
  water_year: z.coerce.number().int().default(CURRENT_WATER_YEAR),
  compare: z
    .preprocess((value) => {
      if (typeof value === "string")
        /* An empty string is what the query-param writer leaves behind when
           the last comparison year is cleared; without the blank filter it
           would read as the year 0. */
        return value
          .split(",")
          .map((part) => part.trim())
          .filter((part) => part !== "")
          .map(Number)
          .filter(Number.isInteger);
      return Array.isArray(value) ? value.map(Number) : [];
    }, z.array(z.number().int()))
    .default([]),
  normal_start: z.coerce.number().int().default(DEFAULT_NORMAL_START),
  /* Both stations are URL-persisted, which is the one thing the chart legend
     cannot do: a legend click hides a series for you, this makes the view
     shareable and drives the embed. Accepts the documented 0 or 1 as well as
     true or false, since the query param writer stringifies what it is
     handed. */
  kapa: z
    .preprocess(
      (value) =>
        value === undefined ? true : !["0", "false", false, 0].includes(value),
      z.boolean()
    )
    .default(true),
  mhfd: z
    .preprocess(
      (value) =>
        value === undefined ? true : !["0", "false", false, 0].includes(value),
      z.boolean()
    )
    .default(true),
  units: z.enum(["f", "c"]).default("f"),
};
